/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Collapse,
  Box,
  InputAdornment,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAuth } from "react-oidc-context";
import styles from "./QuoteMultipleDestinationsPanel.module.css";
import { icons } from "../../svg";
import PinIcon from "../../svg/pin.svg?react";
import { XIcon } from "../../icons";
import { QuotedRateIcon } from "../../icons/QuotedRateIcon";
import { QuotedRateNotifiedIcon } from "../../icons/QuotedRateNotifiedIcon";
import PinWithSlashIcon from "../../svg/pinWithSlash.svg?react";
import { OriginTable } from "./components/OriginTable";
import { useNitroStore } from "../../store/Store";
import {
  FreightTruck,
  IQuotesByDestination,
  ISurroundingLocationsParams,
} from "../../types/IQuotes";
import { useNitroCAStore } from "../../store/CompetitiveAnalysisStore";
import TruckIconFO from "../../icons/TruckIconFO";
import RailIconFO from "../../icons/RailIconFO";
import { NitroMarkerBase } from "../../components/map/components/NitroMarker";
import { toCamelCase } from "../../utils/String";
import { MissingProductTable } from "./components/MissingProductTable";
import {
  getQuoteRowKey,
  isRailFreightQuotedOrNotified,
  normalizeDestinationName,
  resolveNetback,
  sortDestinations,
  sortQuotesByNetbackAndDlvd,
} from "./components/multiLocationQuoteUtils";
import { convertQuotesToRoutes } from "../../utils/convertQuotesToRoutes";
import {
  PricePreference,
  KochDlvdOfferToggle,
} from "./components/KochDlvdOfferToggle";
import { resolveDisplayForQuote } from "./components/pricingDisplay";
import NitroService from "../../utils/nitro.service";
import { IFavoriteLocationExtension } from "../../types/IFavoriteOrigins";
import { CustomButton } from "../../code-connect/component/CustomButton";
import { useQuotesForSurroundingLocations } from "../../hooks/useQuotesForSurroundingLocations";
import { SurroundingLocationsTable } from "./components/SurroundingLocationsTable";

// TODO: fob netback color box is weird size for some values
// TODO: filtering text box while destination is open causes map pin to disappear
// TODO: removing product and other logistics updates does not update quotes in this panel
// TODO: Garden city
// TODO: check expansion logic

export const QuoteMultipleDestinationsPanel: React.FC<{
  destinationQuotes: IQuotesByDestination[];
  favoriteOrigins: IFavoriteLocationExtension[];
  handleOpenOriginManager: () => void;
  initialTruckDlvdOption?: "px" | "alt";
  initialRailDlvdOption?: "px" | "alt";
}> = ({
  destinationQuotes,
  favoriteOrigins,
  handleOpenOriginManager,
  initialTruckDlvdOption,
  initialRailDlvdOption,
}) => {
  const [
    setSelectedLocation,
    setTempPinLocation,
    multiLocationDestinations,
    product,
    currentFilters,
    openInsightsPanel,
    currency,
    uom,
    refetchTrigger,
    setMultiLocationDestinations,
    setShowHidePinsPopup,
    setPanToCoordinates,
  ] = useNitroStore((state) => [
    state.setSelectedLocation,
    state.setTempPinLocation,
    state.multiLocationDestinations,
    state.product,
    state.currentFilters,
    state.openInsightsPanel,
    state.currency,
    state.uom,
    state.refetchSurroundingLocations,
    state.setMultiLocationDestinations,
    state.setShowHidePinsPopup,
    state.setPanToCoordinates,
  ]);
  const refetchQuotes = useNitroCAStore((state) => state.refetchQuotes);
  const [
    setDisplayFreightDetailsPanel,
    triggerReset,
    expandedDestination,
    hiddenOrigins,
    setRailFreightDetailContext,
    setSelectedTruckDetailRecord,
    setDisplayTruckFreightDetailsPanel,
    setAnchorForTruckDetailsPanel,
    setDisplayInboundModeTogglePanel,
    resetExpandRailOriginsWithNoFreight,
    setExpandRailOriginsWithNoFreightTrigger,
    resetExpandTruckOriginsWithNoFreight,
    setExpandTruckOriginsWithNoFreightTrigger,
    setExpandedDestination,
    surroundingQuoteOpen,
    triggerCloseSurroundingQuote,
    setDestinationQuoteOpen,
    closeDestinationQuote,
    expandedDestId,
    setExpandedDestId,
    railTeamNotifiedRows,
  ] = useNitroCAStore((state) => [
    state.setDisplayRailFreightDetailsPanel,
    state.triggerReset,
    state.expandedDestination,
    state.hiddenOrigins,
    state.setRailFreightDetailContext,
    state.setSelectedTruckDetailRecord,
    state.setDisplayTruckFreightDetailsPanel,
    state.setAnchorForTruckDetailsPanel,
    state.setDisplayInboundModeTogglePanel,
    state.resetExpandRailOriginsWithNoFreight,
    state.setExpandRailOriginsWithNoFreightTrigger,
    state.resetExpandTruckOriginsWithNoFreight,
    state.setExpandTruckOriginsWithNoFreightTrigger,
    state.setExpandedDestination,
    state.surroundingQuoteOpen,
    state.triggerCloseSurroundingQuote,
    state.setDestinationQuoteOpen,
    state.closeDestinationQuote,
    state.expandedDestId,
    state.setExpandedDestId,
    state.railTeamNotifiedRows,
  ]);
  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;
  const [filterText, setFilterText] = useState("");
  const [kochPricePreference, setKochPricePreference] =
    useState<PricePreference>({
      truck: "px",
      rail: "px",
    });
  const [isSavingDlvdOptions, setIsSavingDlvdOptions] = useState(false);
  const [showSurroundingLocations, setShowSurroundingLocations] =
    useState(false);
  const [showDestinationsWithoutProduct, setShowDestinationsWithoutProduct] =
    useState(false);
  const [hoveringDestination, setHoveringDestination] = useState({
    isHovering: false,
    id: "",
  });

  // hide the destination header when the user has scrolled into an open origin table
  const [hideDestHeader, setHideDestHeader] = useState(false);
  useEffect(() => {
    setKochPricePreference({
      truck: initialTruckDlvdOption || "px",
      rail: initialRailDlvdOption || "px",
    });
  }, [initialTruckDlvdOption, initialRailDlvdOption]);

  const handleKochDlvdOfferModeChange = (nextMode: PricePreference) => {
    if (isSavingDlvdOptions) {
      return;
    }

    const hasChanged =
      nextMode.truck !== kochPricePreference.truck ||
      nextMode.rail !== kochPricePreference.rail;

    if (!hasChanged) {
      return;
    }

    setKochPricePreference(nextMode);
    setIsSavingDlvdOptions(true);

    NitroService.saveDlvdOptions({
      marketer: user,
      truckDlvdOption: nextMode.truck,
      railDlvdOption: nextMode.rail,
    })
      .catch((error) => {
        console.error("Failed to save dlvd options", error);
      })
      .finally(() => {
        setIsSavingDlvdOptions(false);
      });
  };

  useEffect(() => {
    const scrollContainer = document.getElementById(
      "deskstop-destination-table",
    );
    if (!scrollContainer) return;
    const handleScroll = () => {
      if (!expandedDestId) {
        setHideDestHeader(false);
        return;
      }
      const originTable = scrollContainer.querySelector(
        "[data-origin-table-root]",
      );
      if (!originTable) {
        setHideDestHeader(false);
        return;
      }
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const { top: originTop, bottom: originBottom } =
        originTable.getBoundingClientRect();
      const shouldHide =
        originTop <= containerTop + 36 && originBottom > containerTop;
      setHideDestHeader(shouldHide);
    };
    // check immediately when a row expands/collapses
    handleScroll();
    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [expandedDestId]);
  const queryType =
    currentFilters.city || currentFilters.location ? "location" : "party";

  const getDisplayTitle = () => {
    const whoValue = currentFilters.party;
    let whereValue = currentFilters.city || currentFilters.location;
    if (currentFilters.city) {
      const cityState = currentFilters.city.split(",");
      const city = toCamelCase(cityState[0]);
      const state = cityState[1]?.trim();
      whereValue = `${city}, ${state}`;
    }
    if (whereValue && whoValue) {
      return `${whoValue} - ${whereValue}`;
    }
    if (whereValue) {
      return whereValue;
    }
    if (whoValue) {
      return whoValue;
    }
    return "";
  };
  const applyTextFilter = (entry: IQuotesByDestination) =>
    entry.destination.displayName
      .toLowerCase()
      .includes(filterText.toLowerCase()) ||
    entry.destination.city?.toLowerCase().includes(filterText.toLowerCase()) ||
    entry.destination.stateOrProvince
      ?.toLowerCase()
      .includes(filterText.toLowerCase());

  // load surrounding quotes when panel loads
  const locationParams: ISurroundingLocationsParams = {
    party_id: currentFilters.party,
    citystate_id: currentFilters.city,
    product,
    marketer: user,
    shipDate: currentFilters.month || "",
    currency: currency || "USD",
    uom: uom || "ST",
  };
  // TODO: handle error
  // TODO: can this be combined with primary quotes hook to reduce calls?
  // TODO: add loading for inbound rail adding rail stations

  // only fetches for city filters
  const { surroundingQuotes, loading: isLoadingSurroundingQuotes } =
    useQuotesForSurroundingLocations(locationParams, refetchTrigger);

  // add surrounding quotes to map
  useEffect(() => {
    if (surroundingQuotes.length > 0) {
      // if the expand surrounding locations flag is on then show surrounding locations table and pins on the map
      if (expandedDestination?.expandSurroundingLocations) {
        // slight delay to allow panel to render first to avoid jumpy UI
        setTimeout(() => {
          setShowSurroundingLocations(true);
        }, 100);
        const quotes = [...destinationQuotes, ...surroundingQuotes];
        const multiLocDataCompAnalysis = convertQuotesToRoutes(quotes);
        setMultiLocationDestinations(multiLocDataCompAnalysis);
      }
    }
  }, [destinationQuotes, surroundingQuotes, setMultiLocationDestinations]);

  // TODO: Locations without product up collapse is glitchy with info
  const { filteredDestinationQuotes, destinationsWithoutProduct } =
    useMemo(() => {
      // TODO: make api not return quotes for hidden modes and is this favorite origin mode selection or enabled mode?
      // TODO: need to read location outbound? or update favorite toggles when location mode changes
      // applying mode filters
      const filteredByModes = destinationQuotes.map((entry) => {
        const filteredQuotes = entry.quotes.filter((quote) => {
          const mode = quote.mode.toLowerCase();
          const originKey = `${quote.competitor ? "competitor" : "koch"}-${
            quote.origin?.name
          }`;
          return !hiddenOrigins[mode as "truck" | "rail"].has(originKey);
        });
        return {
          destination: entry.destination,
          quotes: filteredQuotes,
        };
      });

      // applying product filter
      // include city pin records even if they don't have the product (city records don't have that kind of data)
      const filteredByProduct = filteredByModes.filter(
        (entry) =>
          entry.destination.availableProducts.includes(product) ||
          entry.destination.isCity,
      );

      // destinations without product with city pins excluded
      const destWithoutProduct = filteredByModes.filter(
        (entry) =>
          !entry.destination.availableProducts.includes(product) &&
          !entry.destination.isCity,
      );

      // no text filter applied
      if (!filterText) {
        return {
          filteredDestinationQuotes: sortDestinations(
            filteredByProduct,
            queryType,
          ) as IQuotesByDestination[],
          destinationsWithoutProduct: destWithoutProduct,
        };
      }

      const filteredDestinations = sortDestinations(
        filteredByProduct.filter(applyTextFilter),
        queryType,
      ) as IQuotesByDestination[];
      const destinationsWithoutProduct =
        destWithoutProduct.filter(applyTextFilter);

      // applying text filter
      return {
        filteredDestinationQuotes: filteredDestinations,
        destinationsWithoutProduct,
      };
    }, [destinationQuotes, filterText, hiddenOrigins, product]);

  // close panel effect
  useEffect(() => {
    if (closeDestinationQuote <= 0) {
      return;
    }

    setExpandedDestId(undefined);
    setDestinationQuoteOpen(false);
  }, [closeDestinationQuote]);

  // text filter effect
  // TODO: i think that the dropdowns will close when i remove filter. is that supposed to happen?
  useEffect(() => {
    if (filterText && destinationsWithoutProduct.length > 0) {
      setShowDestinationsWithoutProduct(true);
    } else {
      setShowDestinationsWithoutProduct(false);
    }
    if (filterText && surroundingQuotes.length > 0) {
      setShowSurroundingLocations(true);
    } else {
      setShowSurroundingLocations(false);
    }
  }, [destinationsWithoutProduct, filterText, surroundingQuotes]);

  useEffect(() => {
    let quotesToShowOnMap = [
      ...filteredDestinationQuotes,
      ...destinationsWithoutProduct,
    ];
    quotesToShowOnMap =
      surroundingQuotes.length > 0 && showSurroundingLocations
        ? [...quotesToShowOnMap, ...surroundingQuotes.filter(applyTextFilter)]
        : quotesToShowOnMap;
    // reset map pins to only show filtered results
    setMultiLocationDestinations(convertQuotesToRoutes(quotesToShowOnMap));
  }, [showSurroundingLocations, filterText]);

  // re-focus the map on the still-expanded destination when this panel refreshes so its routes don't get lost
  useEffect(() => {
    if (!expandedDestId) {
      return;
    }

    const destinationRecord = destinationQuotes.find(
      (entry) => entry.destination.id === expandedDestId,
    )?.destination;

    if (destinationRecord) {
      setSelectedLocation({
        id: destinationRecord.id,
        name: destinationRecord.name,
        owner: destinationRecord.details?.owner || "",
        address: "",
        city: destinationRecord.city || "",
        state: "",
        zip: "",
        country: "",
        price: null,
        lat: destinationRecord.coordinate?.[0] || 0,
        long: destinationRecord.coordinate?.[1] || 0,
        products: [],
        modes: [],
        carriers: [],
        pinType: "customer" as const,
      });
    }

    // slight delay to allow panel to render first to avoid jumpy UI
    const timeoutId = setTimeout(() => {
      let quotesToShowOnMap = [
        ...filteredDestinationQuotes,
        ...destinationsWithoutProduct,
      ];
      quotesToShowOnMap =
        surroundingQuotes.length > 0 && showSurroundingLocations
          ? [...quotesToShowOnMap, ...surroundingQuotes.filter(applyTextFilter)]
          : quotesToShowOnMap;
      setMultiLocationDestinations(convertQuotesToRoutes(quotesToShowOnMap));
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [destinationQuotes, expandedDestId]);

  // generic function that handles behavior for expanding/collapsing destination rows whether from button click or external trigger
  const handleToggleExpand = (dest: { id: string; name: string }) => {
    // always perform these actions
    setDisplayInboundModeTogglePanel(false);
    setDisplayFreightDetailsPanel(false);
    const isCurrentlyExpanded = expandedDestId === dest.id;
    setExpandedDestId(isCurrentlyExpanded ? undefined : dest.id);

    // only perform these actions when collapsing
    if (isCurrentlyExpanded) {
      setSelectedLocation(undefined);
      setTempPinLocation(undefined);
      setDestinationQuoteOpen(false);
      setShowHidePinsPopup(true);
      return;
    }

    // only perform these actions when expanding
    const destinationRecord = multiLocationDestinations?.find(
      (record: any) =>
        record.destination?.id === dest.id ||
        record.destination?.name === dest.name,
    );

    if (destinationRecord?.destination) {
      const locationRecord = {
        id: destinationRecord.destination.id,
        name: destinationRecord.destination.name,
        owner: destinationRecord.destination.owner || "",
        address: "",
        city: destinationRecord.destination.city || "",
        state: "",
        zip: "",
        country: "",
        price: null,
        lat: destinationRecord.destination.coordinate?.[0] || 0,
        long: destinationRecord.destination.coordinate?.[1] || 0,
        products: [],
        modes: [],
        carriers: [],
        pinType: "customer" as const,
      };

      // when expanding a destination row we want to close surrounding quotes panel if open
      if (surroundingQuoteOpen) {
        triggerCloseSurroundingQuote();
      }
      setShowHidePinsPopup(false);
      setDestinationQuoteOpen(true);
      setSelectedLocation(locationRecord);
      setPanToCoordinates({
        lat: locationRecord.lat,
        lng: locationRecord.long,
        zoom: 0,
      });
      triggerReset();
    }
  };

  // specific function for handling button click to expand/collapse destination rows
  const handleToggleClick = (dest: { id: string; name: string }) => {
    // when user clicks the button we want to reset any expansions set to display
    resetExpandTruckOriginsWithNoFreight();
    resetExpandRailOriginsWithNoFreight();
    const isCurrentlyExpanded = expandedDestId === dest.id;
    if (isCurrentlyExpanded) {
      setExpandedDestination(undefined);
    }
    handleToggleExpand(dest);
  };

  // specific effect for handling external trigger to expand/collapse destination rows
  // currently only use cases are opening truck or rail with no freight separately
  useEffect(() => {
    if (expandedDestination) {
      // opening surrounding locations is handled in separate effect below
      if (expandedDestination.expandSurroundingLocations) {
        return;
      }
      // if the expand rail with no freight flag is on then use generic function without special handling for actual button click
      if (expandedDestination.expandRailWithNoFreight) {
        setExpandRailOriginsWithNoFreightTrigger();
      }
      // if the expand truck with no freight flag is on then use generic function without special handling for actual button click
      if (expandedDestination.expandTruckWithNoFreight) {
        setExpandTruckOriginsWithNoFreightTrigger();
      }
      handleToggleExpand(expandedDestination);
    }
  }, [expandedDestination]);

  // TODO: make header titles stick during scrolling
  // TODO: make surrounding locations table not reach the bottom
  // TODO: find cheapest origin for destination and show that in header
  // TODO: make filter conditional and put bottom line there if not showing up
  // TODO: state and display name should appear
  // TODO: origin panel search color does not change at the same time
  // TODO: type in origin price and quote panel does not update
  // TODO: closing insights panel reloads quotes for some reason
  return (
    <Paper id="panelWrapper" className={styles.panelWrapper} elevation={0}>
      {/* Header */}
      <div id="destinationTitle" className={styles.destinationTitleContainer}>
        <div className={styles.destinationTitleLeft}>
          <img
            src={icons.CityIcon}
            alt="Building Black Icon"
            height={35}
            width={35}
          />
          <span className={styles.destinationTitle}>{getDisplayTitle()}</span>
        </div>

        <div className={styles.destinationHeaderControls}>
          {/* Filter Box */}
          {(currentFilters.city || currentFilters.party) && (
            <Box className={styles.filterBox}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Filter Locations..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className={styles.filterInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        src={icons.SearchMagnifyingGlass}
                        alt="Search Magnifying Glass Icon"
                      />
                    </InputAdornment>
                  ),
                  endAdornment: filterText ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setFilterText("")}
                      >
                        <XIcon size={18} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
          )}

          {/* Toggle for price mode */}
          <KochDlvdOfferToggle
            mode={kochPricePreference}
            setMode={handleKochDlvdOfferModeChange}
            isSaving={isSavingDlvdOptions}
          />
        </div>
      </div>

      {/* Tables */}
      {/* Only show tables if there are favorite origins, otherwise show a message */}
      {favoriteOrigins.length > 0 && (
        <div
          id="deskstop-destination-table"
          style={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Destinations Table */}
          {filteredDestinationQuotes.length > 0 && (
            <TableContainer
              id="destination-table-container"
              className={styles.tableContainerWrapper}
              style={{ overflow: "visible" }}
            >
              <Table stickyHeader>
                <TableHead
                  className={`${styles.destinationTableHead}${
                    hideDestHeader
                      ? ` ${styles.destinationTableHeadHidden}`
                      : ""
                  }`}
                >
                  <TableRow>
                    <TableCell className={styles.headerCell}>
                      Destination
                    </TableCell>
                    <TableCell className={styles.headerCell}>
                      Competitor
                      <br />
                      <span style={{ whiteSpace: "nowrap" }}>DLVD Offer</span>
                    </TableCell>
                    <TableCell className={styles.headerCell}>
                      Koch
                      <br />
                      <span style={{ whiteSpace: "nowrap" }}>DLVD Offer</span>
                    </TableCell>
                    <TableCell
                      className={styles.headerCell}
                      style={{ paddingLeft: "7px" }}
                    >
                      Freight
                    </TableCell>
                    <TableCell className={styles.headerCell}>
                      FOB Netback
                    </TableCell>
                    <TableCell className={styles.headerCell}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody id="destination-table-body">
                  {filteredDestinationQuotes.map((entry) => {
                    const isExpanded = expandedDestId === entry.destination.id;

                    const isHovering =
                      hoveringDestination.isHovering &&
                      hoveringDestination.id === entry.destination.id;
                    const isDestinationNameTruncated =
                      entry.destination.displayName.length > 15;
                    const destinationName = isDestinationNameTruncated
                      ? `${entry.destination.displayName.slice(0, 15)}...`
                      : entry.destination.displayName;
                    const cityState = `${toCamelCase(
                      entry.destination.city || "",
                    )}, ${entry.destination.stateOrProvince || ""}`;

                    // Find the optimal Koch and competitor quote (cheapest valid DLVD, excluding N/O, DISC, no-price)
                    const sortedQuotes = sortQuotesByNetbackAndDlvd(
                      entry.quotes,
                      kochPricePreference,
                    );
                    const cheapestKochQuote = sortedQuotes.find(
                      (quote) =>
                        !quote.competitor &&
                        !!quote.freight &&
                        !quote.freight?.isExternal &&
                        resolveNetback(quote, kochPricePreference) !== null,
                    );
                    const cheapestCompetitorQuote = sortedQuotes.find(
                      (quote) =>
                        quote.competitor &&
                        !!quote.freight &&
                        resolveNetback(quote, kochPricePreference) !== null,
                    );
                    const selectedNetbackQuote =
                      cheapestKochQuote || cheapestCompetitorQuote;

                    const {
                      deliveredPrice,
                      freight,
                      netback,
                      netbackSubText,
                      priceSetter,
                      competitorDeliveredPrice,
                    } = resolveDisplayForQuote(
                      entry.destination,
                      kochPricePreference,
                      selectedNetbackQuote,
                      cheapestKochQuote,
                      cheapestCompetitorQuote,
                    );

                    const { isQuoted, isNotified } =
                      isRailFreightQuotedOrNotified(
                        railTeamNotifiedRows,
                        cheapestKochQuote,
                      );

                    let freightColor = "#475569";
                    if (isNotified) freightColor = "#009F5C";
                    else if (isQuoted) freightColor = "#155DFC";

                    let freightHoverColor = "#2563EB";
                    if (isNotified) freightHoverColor = "#007A47";
                    else if (isQuoted) freightHoverColor = "#1246C2";

                    const freightBg = "transparent";
                    const freightHoverBg = "#E8EFF7";

                    const destinationTooltipSlotProps = {
                      tooltip: {
                        sx: {
                          background: "#ddeefc",
                          color: "#1e3a8a",
                          fontSize: "14px",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#ddeefc",
                        },
                      },
                    };
                    return (
                      <React.Fragment key={entry.destination.id}>
                        <TableRow className={styles.destinationRow}>
                          {/* Destination Cell - if city pin then this cell is not a button since there is no location overview */}
                          <TableCell>
                            {entry.destination.isCity ? (
                              <>
                                {isDestinationNameTruncated ? (
                                  <Tooltip
                                    title={normalizeDestinationName(
                                      entry.destination.displayName,
                                    )}
                                    arrow
                                    slotProps={destinationTooltipSlotProps}
                                  >
                                    <div
                                      style={{ paddingTop: "10px" }}
                                      className={styles.destinationName}
                                    >
                                      {destinationName}
                                    </div>
                                  </Tooltip>
                                ) : (
                                  <div
                                    style={{ paddingTop: "10px" }}
                                    className={styles.destinationName}
                                  >
                                    {destinationName}
                                  </div>
                                )}

                                <Box className={styles.locationCell}>
                                  <NitroMarkerBase
                                    width={20}
                                    height={25}
                                    type={entry.destination.pinType || ""}
                                  />
                                  <span
                                    className={`${styles.destinationLocation} ${
                                      cityState.length > 17
                                        ? styles.longCityStateFontSize
                                        : styles.shortCityStateFontSize
                                    }`}
                                  >
                                    {cityState}
                                  </span>
                                </Box>
                              </>
                            ) : (
                              <Button
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-start",
                                  paddingTop: "10px",
                                  paddingLeft: "0px",
                                  paddingRight: "0px",
                                  paddingBottom: "0px",
                                  "&:hover": { backgroundColor: "transparent" },
                                }}
                                onMouseEnter={() =>
                                  setHoveringDestination({
                                    isHovering: true,
                                    id: entry.destination.id,
                                  })
                                }
                                onMouseLeave={() =>
                                  setHoveringDestination({
                                    isHovering: false,
                                    id: "",
                                  })
                                }
                                onClick={() =>
                                  openInsightsPanel(
                                    entry.destination.id,
                                    "Logistics",
                                    false,
                                  )
                                }
                              >
                                {isDestinationNameTruncated ? (
                                  <Tooltip
                                    title={normalizeDestinationName(
                                      entry.destination.displayName,
                                    )}
                                    arrow
                                    slotProps={destinationTooltipSlotProps}
                                  >
                                    <div
                                      className={
                                        isHovering
                                          ? styles.destinationNameHover
                                          : styles.destinationName
                                      }
                                    >
                                      {normalizeDestinationName(
                                        destinationName,
                                      )}
                                    </div>
                                  </Tooltip>
                                ) : (
                                  <div
                                    className={
                                      isHovering
                                        ? styles.destinationNameHover
                                        : styles.destinationName
                                    }
                                  >
                                    {normalizeDestinationName(destinationName)}
                                  </div>
                                )}

                                <Box className={styles.locationCell}>
                                  <NitroMarkerBase
                                    width={20}
                                    height={25}
                                    type={entry.destination.pinType || ""}
                                  />
                                  <span
                                    className={`${styles.destinationLocation} ${
                                      cityState.length > 17
                                        ? styles.longCityStateFontSize
                                        : styles.shortCityStateFontSize
                                    }`}
                                  >
                                    {toCamelCase(entry.destination.city || "")},{" "}
                                    {entry.destination.stateOrProvince}
                                  </span>
                                </Box>
                              </Button>
                            )}
                          </TableCell>
                          {/* Competitor DLVD Offer Cell */}
                          <TableCell>
                            <span
                              id="competitor-dlvd-offer-header"
                              className={`${styles.destinationFOBNetback} ${
                                priceSetter === "COMPETITOR"
                                  ? styles.dlvdPriceSetterText
                                  : ""
                              }`}
                            >
                              ${competitorDeliveredPrice}
                            </span>
                            {cheapestCompetitorQuote &&
                              competitorDeliveredPrice !== "--" && (
                                <Box className={styles.dlvdOfferSubCell}>
                                  {cheapestCompetitorQuote.mode.toUpperCase() ===
                                    "TRUCK" && (
                                    <TruckIconFO
                                      color="#60A5FA"
                                      paddingTop={4}
                                    />
                                  )}
                                  {cheapestCompetitorQuote.mode.toUpperCase() ===
                                    "RAIL" && (
                                    <RailIconFO
                                      color="#F97316"
                                      paddingTop={4}
                                    />
                                  )}
                                  <span className={styles.dlvdOfferSubText}>
                                    {cheapestCompetitorQuote.origin.name}
                                  </span>
                                </Box>
                              )}
                          </TableCell>
                          {/* Koch DLVD Offer Cell */}
                          <TableCell>
                            <div
                              id="dlvd-price"
                              className={`${styles.priceBold} ${
                                priceSetter === "KOCH"
                                  ? styles.dlvdPriceSetterText
                                  : ""
                              }`}
                            >
                              ${deliveredPrice}
                            </div>
                          </TableCell>
                          {/* Freight Cell */}
                          <TableCell style={{ paddingLeft: "1px" }}>
                            {/* No Freight Value */}
                            {freight === "--" ? (
                              <span className={styles.destinationFreight}>
                                {freight}
                              </span>
                            ) : (
                              <Button
                                id="freight-button"
                                className={styles.freightCell}
                                sx={{
                                  borderRadius: "3px",
                                  color: freightColor,
                                  backgroundColor: freightBg,
                                  "&:hover": {
                                    color: freightHoverColor,
                                    backgroundColor: freightHoverBg,
                                  },
                                }}
                                onClick={(event) => {
                                  if (cheapestKochQuote?.mode === "RAIL") {
                                    setRailFreightDetailContext({
                                      quote: cheapestKochQuote,
                                      destination: entry.destination,
                                      rowKey: getQuoteRowKey(cheapestKochQuote),
                                    });
                                    setDisplayFreightDetailsPanel(true);
                                  }
                                  if (cheapestKochQuote?.mode === "TRUCK") {
                                    setDisplayTruckFreightDetailsPanel(true);
                                    setSelectedTruckDetailRecord(
                                      cheapestKochQuote.freight as FreightTruck,
                                    );
                                    setAnchorForTruckDetailsPanel(
                                      event.currentTarget,
                                    );
                                  }
                                }}
                              >
                                <span className={styles.destinationFreight}>
                                  ${freight}
                                </span>
                                {isQuoted && !isNotified && <QuotedRateIcon />}
                                {isNotified && <QuotedRateNotifiedIcon />}
                              </Button>
                            )}
                          </TableCell>
                          {/* FOB Netback Cell */}
                          <TableCell>
                            <div
                              id="fob-netback-cell"
                              className={styles.priceBold}
                            >
                              {selectedNetbackQuote?.mode.toUpperCase() ===
                                "RAIL" &&
                                selectedNetbackQuote.freight &&
                                "minCars" in selectedNetbackQuote.freight && (
                                  <span className={styles.railCarCount}>
                                    {selectedNetbackQuote.freight.minCars}+{" "}
                                  </span>
                                )}
                              <span
                                id="netback-price"
                                className={styles.destinationFOBNetback}
                              >
                                {netback}
                              </span>
                            </div>
                            {selectedNetbackQuote?.freight && (
                              <Box className={styles.fobSubCell}>
                                {selectedNetbackQuote?.mode.toUpperCase() ===
                                  "TRUCK" && (
                                  <TruckIconFO color="#60A5FA" paddingTop={4} />
                                )}
                                {selectedNetbackQuote?.mode.toUpperCase() ===
                                  "RAIL" && (
                                  <RailIconFO color="#F97316" paddingTop={4} />
                                )}
                                <span className={styles.fobSubText}>
                                  {selectedNetbackQuote?.origin.name}{" "}
                                  {netbackSubText}
                                </span>
                              </Box>
                            )}
                          </TableCell>
                          {/* Actions Cell */}
                          <TableCell>
                            <IconButton
                              id="destination-toggle-button-all-origin"
                              size="small"
                              disableRipple
                              onClick={() =>
                                handleToggleClick(entry.destination)
                              }
                            >
                              {isExpanded ? (
                                <Box
                                  sx={{
                                    backgroundColor: "#DBEAFE",
                                    borderRadius: "6px",
                                    justifyContent: "center",
                                    display: "flex",
                                    alignItems: "center",
                                    height: 24.5,
                                    width: 24.5,
                                  }}
                                >
                                  <KeyboardArrowUp sx={{ color: "#1D4ED8" }} />
                                </Box>
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                          <TableCell
                            colSpan={6}
                            className={styles.collapseCell}
                            sx={{ paddingRight: 0 }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <OriginTable
                                quotes={entry.quotes}
                                destination={entry.destination}
                                resetTrigger={refetchQuotes}
                                kochDlvdOfferMode={kochPricePreference}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Additional Tables */}
          <div style={{ paddingTop: "50px", flexShrink: 0 }}>
            {/* Destinations Without Product Table */}
            {destinationsWithoutProduct.length > 0 && (
              <div className={styles.destWithoutProductMetaContainer}>
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            paddingLeft: 0,
                            paddingRight: 0,
                            borderBottom: "none",
                            background: "rgba(255, 251, 235, 0.50)",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingTop: "12px",
                              paddingBottom: "8px",
                              paddingLeft: "16px",
                              paddingRight: "16px",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PinWithSlashIcon
                                className={styles.destWithoutProductPinIcon}
                              />
                              <span
                                className={styles.surroundingLocationsHeader}
                              >
                                Locations Without {product}
                              </span>
                              <span className={styles.destWithoutProductCount}>
                                {destinationsWithoutProduct.length}
                              </span>
                            </Box>

                            <IconButton
                              size="small"
                              disableRipple
                              onClick={() =>
                                setShowDestinationsWithoutProduct(
                                  !showDestinationsWithoutProduct,
                                )
                              }
                            >
                              {showDestinationsWithoutProduct ? (
                                <Box
                                  sx={{
                                    backgroundColor: "#DBEAFE",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: 24,
                                    width: 24,
                                  }}
                                >
                                  <KeyboardArrowUp sx={{ color: "#1D4ED8" }} />
                                </Box>
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </Box>

                          {showDestinationsWithoutProduct && (
                            <div className={styles.destWithoutProductTip}>
                              Do the following locations accept the product?
                              Click{" "}
                              <span
                                className={styles.destWithoutProductTipBold}
                              >
                                Add Product
                              </span>{" "}
                              to view freight.{" "}
                            </div>
                          )}

                          <Collapse
                            in={showDestinationsWithoutProduct}
                            timeout="auto"
                            unmountOnExit
                          >
                            <MissingProductTable
                              destinations={destinationsWithoutProduct}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}

            {/* Surrounding Locations Table */}
            {currentFilters.city && (
              <div className={styles.surroundingLocationsMetaContainer}>
                <TableContainer style={{ overflow: "visible" }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            paddingLeft: 0,
                            paddingRight: 0,
                            borderBottom: "none",
                            background: "rgba(239, 246, 255, 0.40)",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingTop: "12px",
                              paddingBottom: "8px",
                              paddingLeft: "16px",
                              paddingRight: "16px",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PinIcon
                                className={styles.surroundingLocationsPinIcon}
                              />
                              <span
                                className={styles.surroundingLocationsHeader}
                              >
                                Locations in Surrounding Area
                              </span>
                              {isLoadingSurroundingQuotes && (
                                <span
                                  style={{ marginLeft: 10, padding: "3px" }}
                                >
                                  <CircularProgress size={18} />
                                </span>
                              )}
                              {surroundingQuotes.length > 0 && (
                                <span
                                  className={styles.destWithoutProductCount}
                                >
                                  {
                                    surroundingQuotes.filter(applyTextFilter)
                                      .length
                                  }
                                </span>
                              )}
                            </Box>

                            <IconButton
                              id="destination-toggle-button-surrounding"
                              size="small"
                              disableRipple
                              disabled={isLoadingSurroundingQuotes}
                              onClick={() => {
                                const isOpening = !showSurroundingLocations;
                                const currentlyExpandedDestination =
                                  filteredDestinationQuotes.find(
                                    (entry) =>
                                      entry.destination.id === expandedDestId,
                                  )?.destination;
                                // collapsing surrounding locations resets map to only show pins for primary destination quotes

                                if (showSurroundingLocations) {
                                  setMultiLocationDestinations(
                                    convertQuotesToRoutes(destinationQuotes),
                                  );
                                  if (surroundingQuoteOpen) {
                                    setSelectedLocation(undefined);
                                    setTempPinLocation(undefined);
                                  }
                                }
                                if (!showSurroundingLocations) {
                                  // Opening surrounding locations should close OD-pair map state from destination expansion.
                                  setSelectedLocation(undefined);
                                  setTempPinLocation(undefined);
                                  setDestinationQuoteOpen(false);
                                  setShowHidePinsPopup(true);

                                  const quotes = [
                                    ...destinationQuotes,
                                    ...surroundingQuotes,
                                  ];
                                  const multiLocDataCompAnalysis =
                                    convertQuotesToRoutes(quotes);
                                  setMultiLocationDestinations(
                                    multiLocDataCompAnalysis,
                                  );
                                  // clear the open OriginTable so its routes don't persist over surrounding pins
                                  if (expandedDestId) {
                                    setSelectedLocation(undefined);
                                    setTempPinLocation(undefined);
                                    setExpandedDestId(undefined);
                                    setDestinationQuoteOpen(false);
                                  }
                                }

                                setShowSurroundingLocations(
                                  !showSurroundingLocations,
                                );
                                if (isOpening) {
                                  setExpandedDestination(
                                    {
                                      id:
                                        currentlyExpandedDestination?.id ||
                                        currentFilters.city ||
                                        currentFilters.location ||
                                        "surrounding-locations",
                                      name:
                                        currentlyExpandedDestination?.name ||
                                        "surrounding-locations",
                                    },
                                    { expandSurroundingLocations: true },
                                  );
                                } else {
                                  setExpandedDestination(undefined);
                                }
                              }}
                            >
                              {showSurroundingLocations ? (
                                <Box
                                  sx={{
                                    backgroundColor: "#DBEAFE",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: 24,
                                    width: 24,
                                  }}
                                >
                                  <KeyboardArrowUp sx={{ color: "#1D4ED8" }} />
                                </Box>
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          </Box>

                          <Collapse
                            in={showSurroundingLocations}
                            timeout="auto"
                            unmountOnExit
                          >
                            <SurroundingLocationsTable
                              surroundingQuotes={surroundingQuotes}
                              kochPricePreference={kochPricePreference}
                              filterText={filterText}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No favorite origins message */}
      {!favoriteOrigins.length && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "12px",
            paddingTop: "48px",
            paddingX: "24px",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor:
                "var(--primary-_states-focusVisible, rgba(21, 93, 252, 0.30))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <InfoOutlinedIcon
              sx={{ color: "var(--primary-main, #155DFC)", fontSize: 24 }}
            />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "18px",
              lineHeight: "24px",
              color: "#0F172A",
            }}
          >
            No Origins Added for {product}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              lineHeight: "20px",
              color: "#64748B",
              maxWidth: "392px",
            }}
          >
            To see pricing and location data, you need to add origins with{" "}
            {product} in the Origins Manager tab.
          </Typography>

          <CustomButton
            id="add-origin-tour-message"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              handleOpenOriginManager();
            }}
            sx={{
              marginTop: "4px",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              backgroundColor: "#2563EB",
              "&:hover": { backgroundColor: "#1D4ED8" },
              maxWidth: "250px",
              width: "100%",
            }}
          >
            Go to Origins Manager Tab
          </CustomButton>
        </Box>
      )}
    </Paper>
  );
};
